import { type FC } from "react";
import { Link } from "react-router";

import { useQuery } from "@apollo/client/react";

import { faGear } from "@fortawesome/pro-duotone-svg-icons/faGear";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { MenuButton } from "@headlessui/react";

import { MeDocument } from "@app_schema";

import { type Breadcrumb } from "@application/types/breadcrumb";

import { DASHBOARD_BILLING_LINK } from "@application/navigation/dashboard_billing_link";
import { DASHBOARD_CONNECTIONS_LINK } from "@application/navigation/dashboard_conections_link";
import { DASHBOARD_SETTINGS_LINK } from "@application/navigation/dashboard_settings_link";
import { DASHBOARD_USERS_LINK } from "@application/navigation/dashboard_users_link";
import { LOGOUT_LINK } from "@application/navigation/logout_link";

import { Menu } from "@styled/menu";
import { MenuItem } from "@styled/menu_item";
import { MenuItems } from "@styled/menu_items";
import { MenuSeparator } from "@styled/menu_separator";

const ITEM_CLASS =
  "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-slate-700 hover:bg-slate-50";

const PopItem: FC<{ link: Breadcrumb }> = ({ link }) => (
  <MenuItem>
    <Link to={link.to} className={ITEM_CLASS}>
      {link.icon && (
        <FontAwesomeIcon icon={link.icon} className="text-slate-400" />
      )}
      {link.name}
    </Link>
  </MenuItem>
);

// The settings gear, the rail's stationary anchor. The gear IS settings — the
// popover opens above it (anchored top start) with a SETTINGS header and the
// administration links gated by the same `me.navigation` flags as the old menu.
// Built on the shared Headless `Menu` so it inherits the row ⋯ menu's dismissal
// semantics (Escape, outside-click, single-open).
export const NavGear: FC = () => {
  const { data } = useQuery(MeDocument);
  const navigation = data?.me?.navigation;

  return (
    <Menu as="div" className="relative mt-auto">
      <MenuButton
        title="Settings"
        aria-label="Settings"
        className="flex size-[30px] items-center justify-center rounded-lg border border-slate-200 bg-white text-sm text-slate-500 hover:bg-slate-50"
      >
        <FontAwesomeIcon icon={faGear} />
      </MenuButton>

      <MenuItems anchor="top start" className="z-50">
        <div className="px-2 pt-1.5 pb-1 text-[11px] font-bold tracking-wide text-slate-400 uppercase">
          Settings
        </div>
        {navigation?.users && <PopItem link={DASHBOARD_USERS_LINK} />}
        {navigation?.billing && <PopItem link={DASHBOARD_BILLING_LINK} />}
        {navigation?.connections && (
          <PopItem link={DASHBOARD_CONNECTIONS_LINK} />
        )}
        <MenuSeparator />
        <PopItem
          link={{ ...DASHBOARD_SETTINGS_LINK, name: "Account Settings" }}
        />
        <MenuSeparator />
        <MenuItem>
          <a href={LOGOUT_LINK.to} className={ITEM_CLASS}>
            {LOGOUT_LINK.icon && (
              <FontAwesomeIcon
                icon={LOGOUT_LINK.icon}
                className="text-slate-400"
              />
            )}
            {LOGOUT_LINK.name}
          </a>
        </MenuItem>
      </MenuItems>
    </Menu>
  );
};
